// BACS generate — Wave D action page.
//
// Reads the comma-separated `?receipts=` query param (passed by the
// PaymentRunPage), shows the eligible receipts and totals, takes a payment
// date + optional notes, and POSTs `POST /api/v1/bacs` to create a draft
// run. On success the run is auto-submitted via `POST /:enc_id/submit`,
// and the user is offered the Standard 18 download.
//
// "Generate BACS file" is the primary action: one click creates the run,
// writes the customer's template CSV and marks the receipts paid (all or
// nothing, server side). Receipts whose bank details differ from the
// customer's stored details carry a non-blocking badge, and generating asks
// for confirmation with the count first. Standard 18 stays as an alternative.

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, FileCheck, FileSpreadsheet } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Card } from '../../components/ui/Card/Card';
import { Button } from '../../components/ui/Button/Button';
import { Table, type Column } from '../../components/ui/Table/Table';
import { Badge } from '../../components/ui/Badge/Badge';
import { Modal } from '../../components/ui/Modal/Modal';
import { DatePicker } from '../../components/form/DatePicker';
import { Textarea } from '../../components/form/Textarea';
import {
  useCreateBacsRun,
  useGenerateBacsFile,
  useSubmitBacsRun,
  useUnpaidReceipts,
} from './hooks/useBacs';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useToast } from '../../hooks/useToast';
import type {
  BacsRunDetail,
  BankingDetailsMissingPayload,
  GeneratedBacsFile,
  UnpaidReceiptDto,
} from '../../types/api';
import styles from './BACSGeneratePage.module.css';

function formatMoney(n: number): string {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

async function downloadStandard18(encId: string, reference: string, onError: () => void) {
  try {
    const res = await apiClient.raw.get<Blob>(endpoints.bacs.exportFile(encId), {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reference}.txt`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    onError();
  }
}

async function downloadCsv(encId: string, filename: string, onError: () => void) {
  try {
    const res = await apiClient.raw.get<Blob>(endpoints.bacs.downloadFile(encId), {
      responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  } catch {
    onError();
  }
}

export function BACSGeneratePage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const idsParam = params.get('receipts') ?? '';
  const requestedIds = useMemo(
    () => idsParam.split(',').map((s) => s.trim()).filter(Boolean),
    [idsParam],
  );
  const requestedSet = useMemo(() => new Set(requestedIds), [requestedIds]);

  // Pull all unpaid receipts so we can look up the rows the caller passed in.
  const unpaid = useUnpaidReceipts({ page: 1, limit: 500 });
  const allReceipts = unpaid.data?.receipts ?? [];
  const includedReceipts: UnpaidReceiptDto[] = useMemo(
    () => allReceipts.filter((r) => requestedSet.has(r.enc_id)),
    [allReceipts, requestedSet],
  );

  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [createdRun, setCreatedRun] = useState<BacsRunDetail | null>(null);
  const [missingDetails, setMissingDetails] = useState<BankingDetailsMissingPayload | null>(null);

  const [generatedFile, setGeneratedFile] = useState<GeneratedBacsFile | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const create = useCreateBacsRun();
  const submit = useSubmitBacsRun(createdRun?.enc_id ?? '');
  const generate = useGenerateBacsFile();

  // Auto-submit once the draft run is created.
  useEffect(() => {
    if (!createdRun) return;
    if (createdRun.status !== 'draft') return;
    submit.mutate(undefined, {
      onSuccess: (res) => {
        if (res.data?.run) setCreatedRun(res.data.run);
      },
    });
    // Submit is fire-and-forget per createdRun.enc_id — the mutation hook
    // returned by useSubmitBacsRun is stable across renders.
  }, [createdRun?.enc_id]);

  const totals = useMemo(() => {
    let value = 0;
    let withBanking = 0;
    for (const r of includedReceipts) {
      value += r.amount;
      if (r.has_banking_details) withBanking += 1;
    }
    const differ = includedReceipts.filter((r) => r.bank_details_changed).length;
    return { count: includedReceipts.length, value, withBanking, differ };
  }, [includedReceipts]);

  function runGenerateFile() {
    setConfirmOpen(false);
    setMissingDetails(null);
    generate.mutate(
      { receipt_ids: includedReceipts.map((r) => r.enc_id), payment_date: paymentDate },
      {
        onSuccess: (data) => {
          setGeneratedFile(data.file);
          setCreatedRun(data.run);
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: BankingDetailsMissingPayload } };
          if (e?.response?.data?.code === 'BANKING_DETAILS_MISSING') {
            setMissingDetails(e.response.data);
          }
        },
      },
    );
  }

  function requestGenerateFile() {
    if (includedReceipts.length === 0) {
      toast.error('No receipts selected.');
      return;
    }
    // Non-blocking fraud check: confirm with the count when bank details changed.
    if (totals.differ > 0) setConfirmOpen(true);
    else runGenerateFile();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (includedReceipts.length === 0) {
      toast.error('No receipts selected.');
      return;
    }
    setMissingDetails(null);
    create.mutate(
      {
        payment_date: paymentDate,
        receipt_ids: includedReceipts.map((r) => r.enc_id),
        notes: notes.trim() ? notes.trim() : undefined,
      },
      {
        onSuccess: (res) => {
          const run = res.data?.run;
          if (run) setCreatedRun(run);
        },
        onError: (err: unknown) => {
          // Inspect axios error for structured 422 banking-details error.
          const e = err as {
            response?: { data?: BankingDetailsMissingPayload };
          };
          if (e?.response?.data?.code === 'BANKING_DETAILS_MISSING') {
            setMissingDetails(e.response.data);
          }
        },
      },
    );
  }

  const columns: Column<UnpaidReceiptDto>[] = [
    { key: 'unique_id', header: 'Receipt #' },
    { key: 'car_registration', header: 'Reg' },
    { key: 'customer_name', header: 'Customer' },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (_v, row) => formatMoney(row.amount),
    },
    {
      key: 'has_banking_details',
      header: 'Banking',
      render: (_v, row) =>
        row.has_banking_details ? (
          <span className={styles.ok}>Ready</span>
        ) : (
          <span className={styles.missing}>Missing</span>
        ),
    },
    {
      key: 'bank_details_changed',
      header: 'Bank check',
      render: (_v, row) =>
        row.bank_details_changed ? (
          <Badge variant="warning">Bank details changed</Badge>
        ) : (
          <span className={styles.ok}>—</span>
        ),
    },
  ];

  return (
    <form className={styles.page} onSubmit={handleSubmit}>
      <PageHeader
        title="Generate BACS"
        subtitle="Generate the bank file for the selected approved receipts and mark them paid."
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            type="button"
            onClick={() => navigate('/collections/payment-run')}
          >
            Back
          </Button>
        }
      />

      <div className={styles.kpiRow}>
        <Card>
          <div className={styles.tile}>
            <span className={styles.tileLabel}>Receipts</span>
            <span className={styles.tileValue}>{totals.count}</span>
          </div>
        </Card>
        <Card>
          <div className={styles.tile}>
            <span className={styles.tileLabel}>Banking ready</span>
            <span className={styles.tileValue}>{totals.withBanking}</span>
          </div>
        </Card>
        <Card>
          <div className={styles.tile}>
            <span className={styles.tileLabel}>Total amount</span>
            <span className={styles.tileValue}>{formatMoney(totals.value)}</span>
          </div>
        </Card>
        <Card>
          <div className={styles.tile}>
            <span className={styles.tileLabel}>Bank details changed</span>
            <span className={styles.tileValue}>{totals.differ}</span>
          </div>
        </Card>
      </div>

      {totals.differ > 0 ? (
        <Card title="Check before generating">
          <p role="status">
            {totals.differ} receipt{totals.differ === 1 ? ' has' : 's have'} bank details that differ from, or
            changed, the customer&apos;s stored details. This does not block the file — check the payee is genuine.
          </p>
        </Card>
      ) : null}

      <Card title="Run details">
        <div className={styles.row}>
          <label>
            Payment date
            <DatePicker
              aria-label="Payment date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
            />
          </label>
        </div>
        <Textarea
          aria-label="Notes"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional internal notes…"
        />
      </Card>

      <Card title={`Receipts in this run (${totals.count})`} flush>
        <Table
          columns={columns}
          data={includedReceipts}
          keyField={(row) => row.enc_id}
          loading={unpaid.isLoading}
          emptyState={
            <div className={styles.empty}>
              <p>No receipts selected. Open the Payment Run page and pick receipts to pay.</p>
            </div>
          }
          ariaLabel="Receipts in this BACS run"
        />
      </Card>

      {missingDetails ? (
        <Card title="Banking details missing">
          <p>
            {missingDetails.message ?? 'Some receipts are missing the banking details required for BACS.'}
          </p>
          <ul className={styles.missingList}>
            {missingDetails.receipts.map((r) => (
              <li key={r.receipt_uuid_enc}>
                {r.car_registration} ({r.receipt_reference ?? '—'}): {r.missing.map((m) => m.replace(/_/g, ' ')).join(', ')}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {generatedFile ? (
        <Card title={`BACS file generated — ${generatedFile.filename}`}>
          <p>
            <FileCheck size={16} aria-hidden /> {generatedFile.total_transactions} payment(s),{' '}
            {formatMoney(generatedFile.total_amount)}
            {generatedFile.template_name ? ` · template ${generatedFile.template_name}` : ''}. The receipts are now
            marked paid.
          </p>
          <div className={styles.runActions}>
            <Button
              variant="primary"
              icon={Download}
              onClick={() =>
                void downloadCsv(generatedFile.enc_id, generatedFile.filename, () =>
                  toast.error('Download failed.'),
                )
              }
            >
              Download BACS file
            </Button>
            {createdRun ? (
              <Button
                variant="secondary"
                onClick={() => navigate(`/collections/bacs/${encodeURIComponent(createdRun.enc_id)}`)}
              >
                Open run
              </Button>
            ) : null}
          </div>
        </Card>
      ) : null}

      {createdRun && !generatedFile ? (
        <Card title={`Run ${createdRun.reference}`}>
          <p>
            <FileCheck size={16} aria-hidden /> Status: <strong>{createdRun.status}</strong>
          </p>
          <p>Total: {formatMoney(createdRun.total_amount)} across {createdRun.total_records} record(s).</p>
          <div className={styles.runActions}>
            <Button
              variant="primary"
              icon={Download}
              onClick={() =>
                void downloadStandard18(createdRun.enc_id, createdRun.reference, () =>
                  toast.error('Download failed.'),
                )
              }
            >
              Download Standard 18
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                navigate(`/collections/bacs/${encodeURIComponent(createdRun.enc_id)}`)
              }
            >
              Open run
            </Button>
          </div>
        </Card>
      ) : null}

      {!createdRun ? (
        <div className={styles.footer}>
          <Button variant="ghost" type="button" onClick={() => navigate('/collections/payment-run')}>
            Cancel
          </Button>
          <Button
            variant="secondary"
            type="submit"
            loading={create.isPending}
            disabled={totals.count === 0 || generate.isPending}
          >
            Generate Standard 18
          </Button>
          <Button
            variant="primary"
            type="button"
            icon={FileSpreadsheet}
            loading={generate.isPending}
            disabled={totals.count === 0 || create.isPending}
            onClick={requestGenerateFile}
          >
            Generate BACS file
          </Button>
        </div>
      ) : null}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Bank details changed"
        size="sm"
        footer={
          <>
            <Button variant="secondary" type="button" onClick={() => setConfirmOpen(false)}>
              Go back
            </Button>
            <Button variant="primary" type="button" onClick={runGenerateFile}>
              Generate anyway
            </Button>
          </>
        }
      >
        <p>
          {totals.differ} of {totals.count} receipt{totals.count === 1 ? '' : 's'} have bank details that differ
          from, or changed, the customer&apos;s stored details. Generate the file anyway?
        </p>
      </Modal>
    </form>
  );
}
